import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Star, MapPin, Phone, Mail, Globe,
  Facebook, Instagram, Linkedin, Twitter,
  Sparkles, Send, Clock, Edit, Save, Loader2,
  FileText, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeadProfile = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [contactHistory, setContactHistory] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${leadId}`);
      setLead(response.data);
      setNotes(response.data.notes || '');
      setStatus(response.data.status || 'new');
      if (response.data.ai_analysis) {
        setAnalysis(response.data.ai_analysis);
      }
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate]);

  const fetchContactHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/contacts/${leadId}`);
      setContactHistory(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contact history:', error);
    }
  }, [leadId]);

  useEffect(() => {
    fetchLead();
    fetchContactHistory();
  }, [fetchLead, fetchContactHistory]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API_URL}/ai/analyze`, { lead_id: leadId });
      setAnalysis(response.data.analysis);
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpdateLead = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/leads/${leadId}`, { notes, status });
      toast.success('Lead updated');
      setEditingNotes(false);
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (stat) => {
    const badges = {
      new: 'badge-primary',
      contacted: 'badge-success',
      follow_up: 'badge-warning',
      replied: 'badge-success',
      closed: 'badge-neutral'
    };
    return badges[stat] || 'badge-neutral';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!lead) return null;

  return (
    <DashboardLayout>
      <Header 
        title={lead.business_name}
        subtitle={`${lead.niche} • ${lead.city}, ${lead.state}`}
      />
      
      <div className="p-8" data-testid="lead-profile">
        <Button
          variant="ghost"
          onClick={() => navigate('/leads')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Leads
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Business Card */}
            <div className="card-dashboard">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{lead.business_name}</h2>
                  <p className="text-slate-500">{lead.category}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  <span className="text-lg font-semibold text-slate-700">{lead.google_rating || 'N/A'}</span>
                  <span className="text-slate-400">({lead.review_count} reviews)</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{lead.address}, {lead.city}, {lead.state} {lead.zip_code}</span>
                  </div>
                  {lead.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${lead.phone}`} className="hover:text-indigo-600">{lead.phone}</a>
                      <button onClick={() => copyToClipboard(lead.phone)} className="p-1 hover:bg-slate-100 rounded">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${lead.email}`} className="hover:text-indigo-600">{lead.email}</a>
                      <button onClick={() => copyToClipboard(lead.email)} className="p-1 hover:bg-slate-100 rounded">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a href={lead.website} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600">
                        {lead.website}
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Social Links */}
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-2">Social Media</p>
                  <div className="flex flex-wrap gap-2">
                    {lead.social_links?.facebook && (
                      <a href={lead.social_links.facebook} target="_blank" rel="noopener noreferrer" 
                         className="p-2 bg-slate-100 rounded-lg hover:bg-indigo-100 transition-colors">
                        <Facebook className="w-4 h-4 text-slate-600" />
                      </a>
                    )}
                    {lead.social_links?.instagram && (
                      <a href={lead.social_links.instagram} target="_blank" rel="noopener noreferrer"
                         className="p-2 bg-slate-100 rounded-lg hover:bg-indigo-100 transition-colors">
                        <Instagram className="w-4 h-4 text-slate-600" />
                      </a>
                    )}
                    {lead.social_links?.linkedin && (
                      <a href={lead.social_links.linkedin} target="_blank" rel="noopener noreferrer"
                         className="p-2 bg-slate-100 rounded-lg hover:bg-indigo-100 transition-colors">
                        <Linkedin className="w-4 h-4 text-slate-600" />
                      </a>
                    )}
                    {lead.social_links?.twitter && (
                      <a href={lead.social_links.twitter} target="_blank" rel="noopener noreferrer"
                         className="p-2 bg-slate-100 rounded-lg hover:bg-indigo-100 transition-colors">
                        <Twitter className="w-4 h-4 text-slate-600" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 flex gap-3">
                <Link to={`/ai-tools?lead=${leadId}`}>
                  <Button className="btn-primary" data-testid="generate-email-btn">
                    <Mail className="w-4 h-4 mr-2" />
                    Generate Email
                  </Button>
                </Link>
                <Link to={`/ai-tools?lead=${leadId}&tab=script`}>
                  <Button variant="outline" data-testid="generate-script-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Script
                  </Button>
                </Link>
              </div>
            </div>

            {/* AI Analysis */}
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  AI Analysis
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Refresh Analysis'
                  )}
                </Button>
              </div>

              {analysis ? (
                <div className="space-y-4">
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Online Presence Score</p>
                    <div className="flex items-center gap-3">
                      <div className="text-3xl font-bold text-indigo-700">{analysis.online_presence_score}</div>
                      <div className="flex-1 h-2 bg-indigo-100 rounded-full">
                        <div 
                          className="h-full bg-indigo-600 rounded-full"
                          style={{ width: `${analysis.online_presence_score}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-2">Pain Points</p>
                    <ul className="space-y-2">
                      {analysis.pain_points?.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-1.5 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-2">Recommended Services</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.service_recommendations?.map((service, i) => (
                        <span key={i} className="badge-success">{service}</span>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-700 mb-1">Priority Action</p>
                    <p className="text-sm text-emerald-600">{analysis.priority_action}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Button onClick={handleAnalyze} className="btn-primary" disabled={analyzing}>
                    {analyzing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Run AI Analysis</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Contact History */}
            <div className="card-dashboard">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Contact History
              </h3>

              {contactHistory.length > 0 ? (
                <div className="space-y-4">
                  {contactHistory.map((contact) => (
                    <div key={contact.id} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`badge-${contact.contact_type === 'email' ? 'primary' : 'success'}`}>
                          {contact.contact_type}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {contact.subject && (
                        <p className="text-sm font-medium text-slate-900">{contact.subject}</p>
                      )}
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{contact.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No contact history yet</p>
              )}
            </div>
          </div>

          {/* Sidebar - CRM */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="card-dashboard">
              <h3 className="font-semibold text-slate-900 mb-4">Lead Status</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Status</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="follow_up">Follow Up Needed</SelectItem>
                      <SelectItem value="closed">Closed / Not Interested</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Contacted?</label>
                  <div className="flex items-center gap-2">
                    {lead.contacted ? (
                      <span className="badge-success flex items-center gap-1">
                        <Check className="w-3 h-3" /> Yes
                      </span>
                    ) : (
                      <span className="badge-neutral">Not yet</span>
                    )}
                  </div>
                </div>

                {lead.last_contacted && (
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Last Contacted</label>
                    <p className="text-slate-900">
                      {new Date(lead.last_contacted).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes Card */}
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Notes</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingNotes(!editingNotes)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>

              {editingNotes ? (
                <div className="space-y-3">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this lead..."
                    rows={4}
                    data-testid="notes-textarea"
                  />
                  <Button 
                    onClick={handleUpdateLead}
                    className="w-full btn-primary"
                    disabled={saving}
                    data-testid="save-notes-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">
                  {notes || 'No notes yet. Click edit to add notes.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LeadProfile;
